import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useList, Table, PageTitle } from "./kit";
import { Chip } from "@/components/common";

const TABS = [
  ["withdrawal", "Widerrufe"],
  ["cancellation", "Kündigungen"],
  ["reports", "Gemeldete Inhalte"],
  ["consents", "Einwilligungen"],
  ["emails", "E-Mail-Fehler"],
];

function LegalRequests({ type }) {
  const { data, reload } = useList(`/admin/legal/requests?type=${type}`);
  const act = async (rid, body) => {
    try { await api.post(`/admin/legal/requests/${rid}?type=${type}`, body); toast.success("Aktualisiert"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const statusTone = (s) => ({ received: "info", needs_review: "warn", reviewing: "info", confirmed: "mint", closed: "neutral" }[s] || "neutral");
  const cols = [
    ["Vorgang", (r) => <span className="font-semibold">{r.case_number}</span>],
    ["Name / E-Mail", (r) => <div><p>{r.name}</p><p className="w-muted text-sm">{r.email}</p></div>],
    [type === "withdrawal" ? "Buchung / Partner" : "Vertrag", (r) => (
      <div className="text-sm">{r.booking_code || r.contract_ref || "–"}{r.provider_name && <p className="w-muted">Waschheld: {r.provider_name}</p>}</div>
    )],
    ["Eingang", (r) => <span className="w-muted text-sm">{fmtDateTime(r.received_at)}</span>],
    ["Status", (r) => <div className="space-y-1"><Chip tone={statusTone(r.status)}>{r.status}</Chip><p className="w-muted text-xs">Ergebnis: {r.legal_result || "offen"}</p>{r.confirmation_sent && <p className="w-muted text-xs">✓ Eingangsbestätigung</p>}</div>],
    ["Aktion", (r) => (
      <div className="flex flex-col gap-1">
        <button className="w-btn w-btn-secondary !min-h-[32px] !px-3 text-sm" onClick={() => act(r.id, { status: "reviewing", note: "In Prüfung genommen" })} data-testid={`legal-review-${r.id}`}>In Prüfung</button>
        <button className="w-btn w-btn-secondary !min-h-[32px] !px-3 text-sm" onClick={() => act(r.id, { status: "confirmed", legal_result: "wirksam", note: "Als wirksam bewertet" })} data-testid={`legal-valid-${r.id}`}>Wirksam</button>
        <button className="w-btn w-btn-secondary !min-h-[32px] !px-3 text-sm" onClick={() => act(r.id, { status: "closed", legal_result: "abgelehnt", note: "Abgelehnt / abgeschlossen" })} data-testid={`legal-close-${r.id}`}>Abschließen</button>
      </div>
    )],
  ];
  return <Table cols={cols} rows={data} testId={`legal-${type}-table`} empty="Keine Vorgänge" />;
}

function Reports() {
  const { data, reload } = useList("/admin/reports");
  const act = async (rid, status) => {
    try { await api.post(`/admin/reports/${rid}`, { status, decision: `Status: ${status}` }); toast.success("Aktualisiert"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [
    ["Vorgang", (r) => <span className="font-semibold">{r.case_number}</span>],
    ["Typ / Ziel", (r) => <div className="text-sm"><Chip>{r.target_type}</Chip><p className="w-muted">{r.target_id || "–"}</p></div>],
    ["Grund", (r) => <div className="text-sm"><p className="font-semibold">{r.reason}</p><p className="w-muted">{r.details}</p></div>],
    ["Eingang", (r) => <span className="w-muted text-sm">{fmtDateTime(r.created_at)}</span>],
    ["Status", (r) => <Chip tone={r.status === "open" ? "warn" : "neutral"}>{r.status}</Chip>],
    ["Aktion", (r) => (
      <div className="flex flex-col gap-1">
        <button className="w-btn w-btn-secondary !min-h-[32px] !px-3 text-sm" onClick={() => act(r.id, "removed")} data-testid={`report-remove-${r.id}`}>Entfernt</button>
        <button className="w-btn w-btn-secondary !min-h-[32px] !px-3 text-sm" onClick={() => act(r.id, "kept")} data-testid={`report-keep-${r.id}`}>Behalten</button>
      </div>
    )],
  ];
  return <Table cols={cols} rows={data} testId="reports-table" empty="Keine Meldungen" />;
}

function Consents() {
  const { data } = useList("/admin/legal/consents");
  const cols = [
    ["Zeitpunkt", (r) => <span className="w-muted text-sm">{fmtDateTime(r.created_at)}</span>],
    ["Typ", (r) => <Chip>{r.type}</Chip>],
    ["Buchung", (r) => <span className="text-sm">{r.booking_id?.slice(0, 8) || "–"}</span>],
    ["AGB / Widerruf", (r) => <span className="text-sm">v{r.agb_version ?? "-"} / v{r.widerruf_version ?? "-"}</span>],
    ["Vorzeitiger Start", (r) => r.early_service_start ? <Chip tone="warn">ja</Chip> : <Chip>nein</Chip>],
    ["Erklärung", (r) => <span className="w-muted text-sm">{r.declared_text}</span>],
  ];
  return <Table cols={cols} rows={data} testId="consents-table" empty="Keine Einwilligungen" />;
}

function EmailFailures() {
  const { data } = useList("/admin/legal/email-failures");
  const cols = [
    ["Zeitpunkt", (r) => <span className="w-muted text-sm">{fmtDateTime(r.created_at)}</span>],
    ["An", (r) => r.to],
    ["Betreff", (r) => r.subject],
    ["Fehler", (r) => <span className="text-[var(--w-error)] text-sm">{r.error}</span>],
  ];
  return <Table cols={cols} rows={data} testId="email-failures-table" empty="Keine fehlgeschlagenen E-Mails" />;
}

export function Compliance() {
  const [tab, setTab] = useState("withdrawals");
  return (
    <div data-testid="admin-compliance">
      <PageTitle sub="Widerrufe, gemeldete Inhalte, Einwilligungen und Eingangsbestätigungen – alle Vorgänge sind serverseitig dokumentiert.">Rechtliches &amp; Verbraucherschutz</PageTitle>
      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} aria-pressed={tab === v} data-testid={`compliance-tab-${v}`}
            className={`w-btn !min-h-[40px] !px-4 ${tab === v ? "w-btn-primary" : "w-btn-secondary"}`}>{l}</button>
        ))}
      </div>
      {tab === "withdrawal" && <LegalRequests type="withdrawal" />}
      {tab === "cancellation" && <LegalRequests type="cancellation" />}
      {tab === "reports" && <Reports />}
      {tab === "consents" && <Consents />}
      {tab === "emails" && <EmailFailures />}
    </div>
  );
}
